import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router';

import { useAppStore } from '@/store/app.store';
import { useAssetEditStore } from '@/store/asset-edit.store';
import type { AssetType } from '@repo/types/asset';

type PendingChangesPromptBody = 'navigation' | 'discard';

const pendingChangesPromptBodyTranslationKeys: Record<PendingChangesPromptBody, string> = {
    navigation: 'asset_pending_changes_discard_prompt_body',
    discard: 'asset_pending_changes_review_discard_prompt_body',
};

export type GuardPendingChanges = (action: () => void, promptBody?: PendingChangesPromptBody) => void;

export const runGuardedAction: GuardPendingChanges = (action) => {
    action();
};

export const AssetPendingChangesGuardContext = createContext<GuardPendingChanges>(runGuardedAction);

export const useAssetPendingChangesGuard = (): GuardPendingChanges => useContext(AssetPendingChangesGuardContext);

interface UseAssetPendingChangesNavigationParams {
    assetId: string;
    assetType: AssetType;
}

interface AssetPendingChangesGuard {
    guardPendingChanges: GuardPendingChanges;
    isDiscardPromptVisible: boolean;
    discardPromptBodyTranslationKey: string;
    cancelNavigation: () => void;
    confirmNavigation: () => void;
}

export const useAssetPendingChangesNavigation = ({
    assetId,
    assetType,
}: UseAssetPendingChangesNavigationParams): AssetPendingChangesGuard => {
    const pendingActionRef = useRef<(() => void) | null>(null);
    const [isGuardedActionBlocked, setIsGuardedActionBlocked] = useState(false);
    const [discardPromptBodyTranslationKey, setDiscardPromptBodyTranslationKey] = useState(
        pendingChangesPromptBodyTranslationKeys.navigation
    );
    const clearPendingSubmissions = useAssetEditStore((state) => state.clearPendingSubmissions);
    const exitAssetDetailsEditMode = useAppStore((state) => state.exitAssetDetailsEditMode);
    const hasPendingSubmissions = useAssetEditStore(
        (state) => state.assetId === assetId && state.pendingSubmissions.length > 0
    );
    const hasPendingSubmissionsRef = useRef(hasPendingSubmissions);

    useEffect(() => {
        hasPendingSubmissionsRef.current = hasPendingSubmissions;
    }, [hasPendingSubmissions]);

    useEffect(() => {
        return () => {
            clearPendingSubmissions();
            exitAssetDetailsEditMode();
        };
    }, [assetId, assetType, clearPendingSubmissions, exitAssetDetailsEditMode]);

    const shouldBlockNavigation = useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) =>
            hasPendingSubmissions && currentLocation.pathname !== nextLocation.pathname,
        [hasPendingSubmissions]
    );
    const routeBlocker = useBlocker(shouldBlockNavigation);
    const isRouteNavigationBlocked = routeBlocker.state === 'blocked';

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (hasPendingSubmissionsRef.current) {
                event.preventDefault();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const discardPendingSubmissions = useCallback(() => {
        hasPendingSubmissionsRef.current = false;
        clearPendingSubmissions();
        exitAssetDetailsEditMode();
    }, [clearPendingSubmissions, exitAssetDetailsEditMode]);

    const guardPendingChanges = useCallback<GuardPendingChanges>(
        (action, promptBody = 'navigation') => {
            if (!hasPendingSubmissions) {
                exitAssetDetailsEditMode();
                action();
                return;
            }

            pendingActionRef.current = action;
            setDiscardPromptBodyTranslationKey(pendingChangesPromptBodyTranslationKeys[promptBody]);
            setIsGuardedActionBlocked(true);
        },
        [exitAssetDetailsEditMode, hasPendingSubmissions]
    );

    const cancelNavigation = useCallback(() => {
        pendingActionRef.current = null;
        setDiscardPromptBodyTranslationKey(pendingChangesPromptBodyTranslationKeys.navigation);
        setIsGuardedActionBlocked(false);

        if (isRouteNavigationBlocked) {
            routeBlocker.reset();
        }
    }, [isRouteNavigationBlocked, routeBlocker]);

    const confirmNavigation = useCallback(() => {
        const pendingAction = pendingActionRef.current;

        pendingActionRef.current = null;
        setDiscardPromptBodyTranslationKey(pendingChangesPromptBodyTranslationKeys.navigation);
        setIsGuardedActionBlocked(false);
        discardPendingSubmissions();

        if (pendingAction) {
            pendingAction();
            return;
        }

        if (isRouteNavigationBlocked) {
            routeBlocker.proceed();
        }
    }, [discardPendingSubmissions, isRouteNavigationBlocked, routeBlocker]);

    return {
        guardPendingChanges,
        isDiscardPromptVisible: isRouteNavigationBlocked || isGuardedActionBlocked,
        discardPromptBodyTranslationKey,
        cancelNavigation,
        confirmNavigation,
    };
};
